import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t as tr } from '@/lib/i18n';
import { ArrowLeft, Bold, Code, Heading, ImagePlus, Italic, Link as LinkIcon, Link2, List, Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { KbMarkdown, staffFileResolver, staffImageResolver } from './KbMarkdown';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
const empty = {
    title: '',
    kind: 'article',
    visibility: 'internal',
    category_id: '',
    tags: '',
    body_md: '',
    expires_at: '',
};
// Starter templates — fill the body (and type) so authors don't start blank.
const TEMPLATES = [
    {
        label: 'SOP (procedure)',
        kind: 'sop',
        body: '## Purpose\n\nWhat this procedure achieves.\n\n## Prerequisites\n\n- \n\n## Steps\n\n1. \n2. \n3. \n\n## Verification\n\n- How to confirm it worked.\n',
    },
    {
        label: 'Troubleshooting',
        kind: 'troubleshooting',
        body: '## Symptom\n\nWhat the user sees.\n\n## Cause\n\nWhy it happens.\n\n## Resolution\n\n1. \n2. \n\n## If that does not work\n\n- Escalate to …\n',
    },
    {
        label: 'FAQ',
        kind: 'faq',
        body: '## Question\n\n…\n\n## Answer\n\n…\n',
    },
];
export function KbEditorPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const qc = useQueryClient();
    const toast = useToast();
    const [searchParams] = useSearchParams();
    // Prefill the title from ?title= (the "Draft an article for this search gap"
    // flow from KB Analytics). Create flow only; edit loads the saved article.
    const [form, setForm] = useState(() => {
        const t = searchParams.get('title');
        return t ? { ...empty, title: t } : empty;
    });
    const taRef = useRef(null);
    const cats = useQuery({ queryKey: ['kb-categories'], queryFn: () => api.get('/kb/categories') });
    const existing = useQuery({
        queryKey: ['kb-article', id],
        queryFn: () => api.get(`/kb/articles/${id}`),
        enabled: isEdit,
    });
    useEffect(() => {
        if (existing.data) {
            const a = existing.data;
            setForm({
                title: a.title,
                kind: a.kind,
                visibility: a.visibility,
                category_id: a.category_id ?? '',
                tags: a.tags.join(', '),
                body_md: a.body_md,
                expires_at: a.expires_at ? a.expires_at.slice(0, 10) : '',
            });
        }
    }, [existing.data]);
    const save = useMutation({
        mutationFn: (body) => isEdit ? api.patch(`/kb/articles/${id}`, body) : api.post('/kb/articles', body),
        onSuccess: (saved) => {
            qc.invalidateQueries({ queryKey: ['kb-articles'] });
            qc.invalidateQueries({ queryKey: ['kb-article', saved.id] });
            toast.success(isEdit ? 'Article saved' : 'Draft created');
            navigate(`/kb/articles/${saved.id}`);
        },
        onError: (e) => toast.error(tr('Could not save'), e.message),
    });
    const submit = (e) => {
        e.preventDefault();
        const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
        const body = {
            title: form.title.trim(),
            kind: form.kind,
            visibility: form.visibility,
            body_md: form.body_md,
            tags,
            expires_at: form.expires_at ? new Date(`${form.expires_at}T00:00:00Z`).toISOString() : null,
        };
        if (form.category_id)
            body.category_id = form.category_id;
        save.mutate(body);
    };
    // Insert/wrap Markdown around the current textarea selection.
    const surround = (before, after = before) => {
        const ta = taRef.current;
        if (!ta)
            return;
        const s = ta.selectionStart ?? form.body_md.length;
        const en = ta.selectionEnd ?? s;
        const val = form.body_md;
        const next = val.slice(0, s) + before + val.slice(s, en) + after + val.slice(en);
        setForm((f) => ({ ...f, body_md: next }));
        requestAnimationFrame(() => {
            ta.focus();
            ta.selectionStart = s + before.length;
            ta.selectionEnd = en + before.length;
        });
    };
    const insertAtCursor = (textVal) => {
        const ta = taRef.current;
        const s = ta?.selectionStart ?? form.body_md.length;
        setForm((f) => ({ ...f, body_md: f.body_md.slice(0, s) + textVal + f.body_md.slice(s) }));
        requestAnimationFrame(() => {
            ta?.focus();
            if (ta)
                ta.selectionStart = ta.selectionEnd = s + textVal.length;
        });
    };
    const applyTemplate = (label) => {
        const t = TEMPLATES.find((x) => x.label === label);
        if (!t)
            return;
        if (form.body_md.trim() && !confirm('Replace the current content with this template?'))
            return;
        setForm((f) => ({ ...f, kind: t.kind, body_md: t.body }));
    };
    // Upload an image straight to Blob (signed URL, same pattern as visit photos)
    // and insert a `kbimg:<name>` marker; readers resolve it to a signed URL.
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const uploadImage = async (file) => {
        if (!file.type.startsWith('image/')) {
            toast.error(tr('Only image files can be inserted'));
            return;
        }
        setUploading(true);
        try {
            const init = await api.post('/kb/images', {
                filename: file.name || 'image.png',
                content_type: file.type,
            });
            const res = await fetch(init.upload_url, {
                method: 'PUT',
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
                body: file,
            });
            if (!res.ok)
                throw new Error(`Upload failed (${res.status})`);
            insertAtCursor(`\n![${(file.name || 'image').replace(/[[\]]/g, '')}](kbimg:${init.name})\n`);
            toast.success(tr('Image inserted'));
        }
        catch (e) {
            toast.error(tr('Could not upload image'), e.message);
        }
        finally {
            setUploading(false);
        }
    };
    const onPaste = (e) => {
        const img = Array.from(e.clipboardData.items).find((it) => it.type.startsWith('image/'));
        const file = img?.getAsFile();
        if (file) {
            e.preventDefault();
            void uploadImage(file);
        }
    };
    // Attach a non-image file (PDF, config, log…). Inserts a `kbfile:<name>` link;
    // readers resolve it to a signed download URL on click. Executable types are
    // rejected server-side.
    const attachRef = useRef(null);
    const uploadFile = async (file) => {
        setUploading(true);
        try {
            const init = await api.post('/kb/files', {
                filename: file.name || 'file',
            });
            const res = await fetch(init.upload_url, {
                method: 'PUT',
                headers: { 'x-ms-blob-type': 'BlockBlob', ...(file.type ? { 'Content-Type': file.type } : {}) },
                body: file,
            });
            if (!res.ok)
                throw new Error(`Upload failed (${res.status})`);
            insertAtCursor(`\n[${init.label.replace(/[[\]]/g, '')}](kbfile:${init.name})\n`);
            toast.success(tr('File attached'));
        }
        catch (e) {
            toast.error(tr('Could not attach file'), e.message);
        }
        finally {
            setUploading(false);
        }
    };
    // "Link to another article" picker — searches the KB and inserts an in-app
    // link `[Title](/kb/articles/<id>)` that SPA-navigates (connected knowledge).
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkQuery, setLinkQuery] = useState('');
    const linkResults = useQuery({
        queryKey: ['kb-link-picker', linkQuery],
        queryFn: () => api.get(`/kb/articles?limit=8${linkQuery.trim() ? `&q=${encodeURIComponent(linkQuery.trim())}` : ''}`),
        enabled: linkOpen,
    });
    const insertArticleLink = (a) => {
        insertAtCursor(`[${a.title.replace(/[[\]]/g, '')}](/kb/articles/${a.id})`);
        setLinkOpen(false);
        setLinkQuery('');
    };
    const TOOLS = [
        { icon: _jsx(Bold, { size: 14 }), title: 'Bold', fn: () => surround('**') },
        { icon: _jsx(Italic, { size: 14 }), title: 'Italic', fn: () => surround('_') },
        { icon: _jsx(Heading, { size: 14 }), title: 'Heading', fn: () => insertAtCursor('\n## ') },
        { icon: _jsx(List, { size: 14 }), title: 'List item', fn: () => insertAtCursor('\n- ') },
        { icon: _jsx(LinkIcon, { size: 14 }), title: 'Link', fn: () => surround('[', '](https://)') },
        { icon: _jsx(Link2, { size: 14 }), title: 'Link to article', fn: () => setLinkOpen(true) },
        { icon: _jsx(Code, { size: 14 }), title: 'Code', fn: () => surround('`') },
        { icon: _jsx(ImagePlus, { size: 14 }), title: 'Insert image', fn: () => fileRef.current?.click() },
        { icon: _jsx(Paperclip, { size: 14 }), title: 'Attach file', fn: () => attachRef.current?.click() },
    ];
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: isEdit ? 'Edit article' : 'New article', description: tr('Author an SOP, procedure, or how-to. Save as a draft; a manager publishes it when ready.'), actions: _jsxs(Link, { to: isEdit ? `/kb/articles/${id}` : '/kb', className: "inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800", children: [_jsx(ArrowLeft, { size: 14 }), tr('Cancel')] }) }), _jsx(PageBody, { children: _jsx("div", { className: "mx-auto max-w-5xl", children: _jsx(Card, { children: _jsx(CardBody, { children: _jsxs("form", { onSubmit: submit, className: "space-y-4", children: [_jsx(Field, { label: tr('Title'), required: true, children: _jsx(Input, { value: form.title, onChange: (e) => setForm((f) => ({ ...f, title: e.target.value })), autoFocus: true, required: true, minLength: 2, placeholder: tr('e.g. Standard laptop build procedure') }) }), _jsxs("div", { className: "grid gap-4 md:grid-cols-4", children: [_jsx(Field, { label: tr('Type'), children: _jsxs(Select, { value: form.kind, onChange: (e) => setForm((f) => ({ ...f, kind: e.target.value })), children: [_jsx("option", { value: "article", children: tr('Article') }), _jsx("option", { value: "sop", children: tr('SOP (procedure)') }), _jsx("option", { value: "troubleshooting", children: tr('Troubleshooting') }), _jsx("option", { value: "faq", children: tr('FAQ') })] }) }), _jsx(Field, { label: tr('Visibility'), hint: tr('Public = also shown to customers.'), children: _jsxs(Select, { value: form.visibility, onChange: (e) => setForm((f) => ({ ...f, visibility: e.target.value })), children: [_jsx("option", { value: "internal", children: tr('Internal (staff only)') }), _jsx("option", { value: "public", children: tr('Public (customers too)') })] }) }), _jsx(Field, { label: tr('Category'), children: _jsxs(Select, { value: form.category_id, onChange: (e) => setForm((f) => ({ ...f, category_id: e.target.value })), children: [_jsx("option", { value: "", children: "\u2014 None \u2014" }), (cats.data ?? []).map((c) => _jsx("option", { value: c.id, children: c.name }, c.id))] }) }), _jsx(Field, { label: tr('Review by'), hint: tr('Flags the article for review after this date.'), children: _jsx(Input, { type: "date", value: form.expires_at, onChange: (e) => setForm((f) => ({ ...f, expires_at: e.target.value })) }) })] }), _jsx(Field, { label: tr('Tags'), hint: tr('Comma-separated, e.g. laptop, imac, networking'), children: _jsx(Input, { value: form.tags, onChange: (e) => setForm((f) => ({ ...f, tags: e.target.value })), placeholder: tr('laptop, build, euc') }) }), _jsxs("div", { children: [_jsxs("div", { className: "mb-1.5 flex flex-wrap items-center justify-between gap-2", children: [_jsx("label", { className: "text-sm font-medium text-slate-700 dark:text-slate-300", children: "Content" }), _jsxs(Select, { value: "", onChange: (e) => { applyTemplate(e.target.value); e.target.value = ''; }, className: "w-48", children: [_jsx("option", { value: "", children: tr('Start from a template…') }), TEMPLATES.map((t) => _jsx("option", { value: t.label, children: t.label }, t.label))] })] }), _jsxs("div", { className: "grid gap-3 lg:grid-cols-2", children: [_jsxs("div", { children: [_jsxs("div", { className: "mb-1 flex items-center gap-1 rounded-t-md border border-b-0 border-slate-200 bg-slate-50 px-1.5 py-1 dark:border-slate-700 dark:bg-slate-800/60", children: [TOOLS.map((t) => (_jsx("button", { type: "button", title: t.title, onClick: t.fn, className: "rounded p-1 text-slate-500 hover:bg-white hover:text-slate-800 dark:hover:bg-slate-700 dark:hover:text-slate-100", children: t.icon }, t.title))), _jsx("span", { className: "ml-auto pr-1 text-[11px] text-slate-400", children: uploading ? 'Uploading…' : 'Markdown · paste an image' })] }), _jsx("input", { ref: fileRef, type: "file", accept: "image/*", className: "hidden", onChange: (e) => { const f = e.target.files?.[0]; if (f)
                                                                    void uploadImage(f); e.target.value = ''; } }), _jsx("input", { ref: attachRef, type: "file", className: "hidden", onChange: (e) => { const f = e.target.files?.[0]; if (f)
                                                                    void uploadFile(f); e.target.value = ''; } }), _jsx(Textarea, { ref: taRef, value: form.body_md, onChange: (e) => setForm((f) => ({ ...f, body_md: e.target.value })), onPaste: onPaste, rows: 18, className: "rounded-t-none font-mono text-[13px]", placeholder: '## Steps\n\n1. …\n2. …' })] }), _jsxs("div", { children: [_jsx("div", { className: "mb-1 rounded-t-md border border-b-0 border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800/60", children: tr('Preview') }), _jsx("div", { className: cn('h-[calc(18*1.5rem)] overflow-auto rounded-b-md rounded-t-none border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900'), children: form.body_md.trim() ? (_jsx("div", { className: "prose-help break-words text-sm", children: _jsx(KbMarkdown, { body: form.body_md, resolvePath: staffImageResolver, resolveFilePath: staffFileResolver }) })) : (_jsx("p", { className: "text-xs italic text-slate-400", children: "Your formatted article appears here." })) })] })] })] }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: () => navigate(isEdit ? `/kb/articles/${id}` : '/kb'), children: tr('Cancel') }), _jsx(Button, { type: "submit", disabled: save.isPending || form.title.trim().length < 2, children: save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create draft' })] })] }) }) }) }) }), _jsx(Modal, { open: linkOpen, onClose: () => setLinkOpen(false), title: tr('Link to an article'), size: "md", children: _jsxs("div", { className: "space-y-3", children: [_jsx(Input, { autoFocus: true, value: linkQuery, onChange: (e) => setLinkQuery(e.target.value), placeholder: tr('Search the knowledge base…') }), _jsx("div", { className: "max-h-72 space-y-1 overflow-auto", children: linkResults.isLoading ? (_jsx("p", { className: "px-1 py-2 text-xs text-slate-400", children: "Searching\u2026" })) : (linkResults.data ?? []).length === 0 ? (_jsx("p", { className: "px-1 py-2 text-xs text-slate-400", children: "No matching articles." })) : ((linkResults.data ?? []).map((a) => (_jsxs("button", { type: "button", onClick: () => insertArticleLink(a), className: "flex w-full flex-col items-start rounded px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800", children: [_jsx("span", { className: "text-sm font-medium text-slate-800 dark:text-slate-100", children: a.title }), _jsx("span", { className: "text-xs text-slate-400", children: a.status })] }, a.id)))) })] }) })] }));
}
